/* eslint-disable no-nested-ternary */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/no-array-index-key */
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import moment from 'moment-timezone';
import { find } from 'lodash-es';
import {
  formatDateYYYYMMDD,
  formatDateMMDDYY,
  formatDateMMDD,
  formatPimDate,
} from '@mummssoftware/utils/formatters';
import {
  DIRECT_CARE,
  emptyOption,
  SmartChart,
  SmartChartIPU,
} from '@mummssoftware/utils/constants';
import {
  GlobalContext,
  SET_APP_VISIT,
  openVisit,
  attemptedVisit,
} from '@mummssoftware/global-context/GlobalContext';
import {
  VisitsGlobalContext,
  SET_REFRESH_VISIT,
} from '@mummssoftware/global-context/VisitsGlobalContext';
import {
  getUsersGeolocation,
  handleGeolocationError,
} from '@mummssoftware/utils/web';
import { Loader } from '@mummssoftware/common-ui';
import { dateToMoment } from '@mummssoftware/utils/dates';
import { RadioVisitMenu } from './RadioVisitMenu';
import { Fees } from './Fees';
import {
  InProgress,
  Completed,
  SET_SAVED_VISIT,
  SET_INITIAL_SAVED_VISIT,
  OptionType,
  levelOfCares,
  SET_VISIT_TITLE,
} from '../Context/constants';
import { VisitContext } from '../Context/contexts/VisitContext';
import { VisitIntro } from './VisitIntro';
import { VisitSubmitButtons } from './VisitSubmitButtons';
import { Context } from '../../context';
import { TextDiv, AppVisitCenter } from '../../components';
import defaults from '../../translations/en.json';
import { CreateVisitErrorMessage } from './CreateVisitErrorMessage';
import { getCreateVisitErrorMessage } from './utils';

export const StartVisit = () => {
  const { appVisit, levelOfCare } = useContext(GlobalContext);
  const globalContext = useContext(GlobalContext);
  const visitsGlobalContext = useContext(VisitsGlobalContext);

  const { pim, currentUser, patient, agency } = useContext(Context);
  const [attptLoader, setAttptLoader] = useState<boolean>(false);
  const [createErrorDetail, setCreateErrorDetail] = useState<string | null>(
    null,
  );
  const {
    dispatchVisit,
    roleGroups,
    primaryRole,
    actingAsRole,
    IPUVisit,
    ipuRadioChoice,
    saveIPUVisitNote,
    processAttemptedVisit,
    travelMileage,
    transitFees,
    travelDuration,
    tollsFee,
    reset,
  } = useContext(VisitContext);
  const isAttemptedVisit = levelOfCare === attemptedVisit;

  const titleMap: any = {
    [openVisit]: 'openVisit',
  };

  useEffect(() => {
    // load initial and needed facilities (array) and roleGroups if not documenting to a previous visit record
    // Check which menu Item was selected in VisitMenu/index and set Dialog header dynamically
    // and perform any additional changes as needed
    if (appVisit) {
      // Open PRN Visit
      dispatchVisit({
        type: SET_VISIT_TITLE,
        payload: (
          <FormattedMessage
            {...defaults.openedVisit}
            values={{
              date: appVisit
                ? formatDateMMDD(appVisit?.effectivedatetime_shifted)
                : null,
            }}
          />
        ),
      });
    } else if (levelOfCare === openVisit) {
      // Open PRN Visit
      dispatchVisit({
        type: SET_VISIT_TITLE,
        payload: defaults.openVisit,
      });
    } else if (isAttemptedVisit) {
      // Open Attempted Visit
      dispatchVisit({
        type: SET_VISIT_TITLE,
        payload: defaults.recordAttemptedVisit,
      });
    } else if (IPUVisit) {
      // Open IPU Visit
      dispatchVisit({
        type: SET_VISIT_TITLE,
        payload: defaults.openIPU,
      });
    } else {
      // Unexpected LOC: StartVisit should only mount for open / attempted / IPU paths.
      dispatchVisit({
        type: SET_VISIT_TITLE,
        payload: defaults.visitError,
      });
    }
  }, []);

  // [Bereavement, Doctor, Aide, Nurse]
  const [roleTypeOption, setRoleTypeOption] = useState<OptionType>(
    find(roleGroups, {
      rolegroup: actingAsRole || primaryRole,
    }) || emptyOption,
  );

  // siteTimezone is now set in GlobalContext
  const tz = moment.tz.guess();

  const siteTimezone = (currentUser && currentUser.siteTimezone) || tz;

  const nowDate = moment().tz(siteTimezone);

  const [visitDate, setVisitDate] = useState<any>(nowDate);
  const visitDateTz: any = nowDate;

  const getTitle = () => {
    if (levelOfCare) {
      if (appVisit) {
        return defaults.recording;
      }
      return (defaults as any)[titleMap[levelOfCare]];
    }
    return '';
  };

  // todo
  const getRole = () => {
    if ((actingAsRole || primaryRole) && roleGroups) {
      const role = find(roleGroups, {
        rolename: actingAsRole || primaryRole,
      }); // check actingAsRole first
      setRoleTypeOption(role || emptyOption); // roleOptionType (determines facilities, visit types, etc)
    }
  };

  useEffect(() => {
    // condition?
    getRole(); // reset roleTypeOption based on actingAsRole or Primary
  }, [primaryRole, actingAsRole, roleGroups && roleGroups.length]);

  // [In Person, Phone]
  const [locationTypeOption, setLocationTypeOption] = useState<OptionType>(
    emptyOption,
  );

  const [isDisabled, setDisabled] = useState<boolean>(true);
  const [isIPUDisabled, setIPUDisabled] = useState<boolean>(true);

  const checkVariables = (): boolean => {
    const bool: boolean =
      !visitDate ||
      (roleTypeOption && !roleTypeOption.label) ||
      (locationTypeOption && !locationTypeOption.label);

    return bool;
  };

  const checkIPUVariables = (): boolean => {
    const bool: boolean = !visitDate || !ipuRadioChoice;

    return bool;
  };

  const onCreateVisitFailure = (errorMessage: string = defaults.visitError) => {
    setDisabled(checkVariables());
    setIPUDisabled(checkIPUVariables());
    if (isAttemptedVisit) {
      setAttptLoader(false);
    }
    // Short header stays generic; actionable detail is a persistent form error (not a toast).
    setCreateErrorDetail(errorMessage);
    dispatchVisit({
      type: SET_VISIT_TITLE,
      payload: defaults.visitError,
    });
  };

  /** @returns true when the visit was created; false on 207 / network failure */
  const onStartVisit = async (): Promise<boolean> => {
    setCreateErrorDetail(null);
    setDisabled(true);
    setIPUDisabled(true);
    if (isAttemptedVisit) {
      setAttptLoader(true);
    }
    const patientLevelOfCare = levelOfCares.filter(
      (r: any) => r.id === patient?.loc,
    );
    const patientLevelOfCareName = patientLevelOfCare[0].label;

    const arrivalCoords = await getUsersGeolocation().catch(
      (error: PositionError) => handleGeolocationError(error),
    );
    const visitTypeDTO = isAttemptedVisit
      ? ipuRadioChoice
      : IPUVisit
      ? DIRECT_CARE
      : (locationTypeOption as any).value;
    const feeDTO = isAttemptedVisit
      ? { travelMileage, transitFees, travelDuration, tolls: tollsFee }
      : {};

    const visitDTO: mumms.PatientVisitDTO = {
      billable: null,
      careProviderNumber: currentUser.personNumber,
      careProviderRole:
        (roleTypeOption as any).roleshortname.toString() || null,
      careProviderRoleGroup: roleTypeOption.label.toString() || null,
      duration: 0,
      faceToFaceVisit: false,
      isscheduled: false,
      levelofcareDisplayValue: patient?.loc || null, // RTN
      starteventdatetime: formatPimDate(moment()), //  moment.tz
      startDate: formatPimDate(
        visitDate.creationData ? visitDate : dateToMoment(visitDate),
      ), //  moment.tz
      status: isAttemptedVisit ? Completed : InProgress,
      visitType: visitTypeDTO, // DIRECT_CARE
      displayvisittype: (locationTypeOption as any).value,
      // TODO PIM changes required to accept value other than SC
      source: IPUVisit ? SmartChartIPU : SmartChart,
      // add visit attributes when saving visit
      visitAttributes: [] as mumms.VisitAttribute[],
      ...feeDTO,
    };

    if (
      arrivalCoords &&
      arrivalCoords.coords &&
      arrivalCoords.coords.longitude &&
      arrivalCoords.coords.latitude
    ) {
      visitDTO.arriveLat = arrivalCoords.coords.latitude.toString() || null;
      visitDTO.arriveLon = arrivalCoords.coords.longitude.toString() || null;
    }
    // endpoint requires sending the visit DTO as a list.
    const patientVisitList = [visitDTO];

    try {
      const res: any = await pim.createPatientVisits(
        patient?.patientNumber as any,
        patientVisitList as any,
        agency,
      );
      // if res is successfull than answer is [{}]
      if (!(res instanceof Response) && res.code !== 207) {
        const visitRes = res[0];
        const {
          careProviderRole,
          careProviderRoleGroup,
          id,
          displayvisittype,
          visitType,
          faceToFaceVisit,
          source,
          visitAttributes,
        } = visitRes;
        // TODO: Refactor to use more props from response into state
        const newVisit = {
          visittype: visitType,
          levelofcare: patientLevelOfCareName,
          levelofcareshortname: patient?.loc,
          effectivedate: formatDateYYYYMMDD(visitRes.startDate),
          effectivedatetime: formatPimDate(visitRes.startDate),
          location: locationTypeOption.label,
          id,
          careProviderRole,
          careProviderRoleGroup,
          displayvisittype,
          // Update F2F attribute based on response prop
          facetofacevisit: faceToFaceVisit,
          isfacetofaceallowed: visitRes.isFaceToFaceAllowed,
          // add visit attributes when saving visit
          source,
          visitAttributes,
        };

        if (isAttemptedVisit) {
          processAttemptedVisit();
          // reset removes APP_VISIT from Global Context
          reset();
          setAttptLoader(false);
          // setting APP_VISIT in Global Context
          globalContext.dispatch({
            type: SET_APP_VISIT,
            payload: newVisit,
          });
        } else {
          globalContext.dispatch({
            type: SET_APP_VISIT,
            payload: newVisit,
          });
          dispatchVisit({
            type: SET_SAVED_VISIT,
            payload: visitRes,
          });
          dispatchVisit({
            type: SET_INITIAL_SAVED_VISIT,
            payload: visitRes,
          });
          if (IPUVisit) {
            saveIPUVisitNote(visitRes);
          }
          // Refresh visit log only. VisitContext skips loadPriorVisitsOnMount when
          // savedVisit/appVisit are already set, so we do not re-hydrate this visit.
          visitsGlobalContext.dispatchVG({
            type: SET_REFRESH_VISIT,
          });
        }
        return true;
      }
      onCreateVisitFailure(getCreateVisitErrorMessage(res));
      return false;
    } catch (e) {
      console.error('e', e);
      onCreateVisitFailure();
      return false;
    }
  };

  useEffect(() => {
    setDisabled(checkVariables);
  }, [roleTypeOption.label, locationTypeOption]);

  useEffect(() => {
    setIPUDisabled(checkIPUVariables);
  }, [ipuRadioChoice]);

  const appVisitText =
    appVisit &&
    `${appVisit.levelofcareshortname} on ${formatDateMMDDYY(
      appVisit.effectivedatetime,
    )}`;

  const getStartVisitUI = () => {
    if (IPUVisit) {
      return (
        <Fragment>
          <VisitIntro
            {...{
              siteTimezone,
              setRoleTypeOption,
              setLocationTypeOption,
              roleTypeOption,
              locationTypeOption,
              setVisitDate,
              visitDate,
            }}
          />
          <RadioVisitMenu ipuVisitChoices={defaults.ipuVisitChoice} />
          <VisitSubmitButtons
            {...{
              onStartVisit,
              isIPUDisabled,
              visitDate,
              siteTimezone: { siteTimezone },
            }}
          />
        </Fragment>
      );
    } else if (isAttemptedVisit) {
      return (
        <Fragment>
          {attptLoader && <Loader />}
          <VisitIntro
            {...{
              siteTimezone,
              setRoleTypeOption,
              setLocationTypeOption,
              roleTypeOption,
              locationTypeOption,
              setVisitDate,
              visitDate,
            }}
          />
          <Fees />
          <RadioVisitMenu ipuVisitChoices={defaults.attemptedVisitChoice} />
          <VisitSubmitButtons
            {...{
              onStartVisit,
              isIPUDisabled,
              visitDate,
            }}
          />
        </Fragment>
      );
    }
    return (
      <Fragment>
        {appVisit && (
          <Fragment>
            <TextDiv>{getTitle()}</TextDiv>
            <AppVisitCenter>
              <div>{appVisitText}</div>
            </AppVisitCenter>
          </Fragment>
        )}
        {!appVisit && (
          <Fragment>
            <VisitIntro
              {...{
                siteTimezone,
                setRoleTypeOption,
                setLocationTypeOption,
                roleTypeOption,
                locationTypeOption,
                setVisitDate,
                visitDate,
              }}
            />
            <VisitSubmitButtons
              {...{
                onStartVisit,
                isDisabled,
                visitDate,
              }}
            />
          </Fragment>
        )}
      </Fragment>
    );
  };

  return (
    <Fragment>
      <CreateVisitErrorMessage message={createErrorDetail} />
      {getStartVisitUI()}
    </Fragment>
  );
};
export default StartVisit;
